// Partner Color Mapping
// Defines consistent colors for partners across all charts and visualizations

export const PARTNER_COLORS = {
  "Ang Kok Keong": {
    backgroundColor: "#3B82F6", // Blue
    borderColor: "#1D4ED8"
  },
  "Chew Wei Jim": {
    backgroundColor: "#10B981", // Green
    borderColor: "#047857"
  },
  "Jonathan Lim": {
    backgroundColor: "#F59E0B", // Amber
    borderColor: "#D97706"
  },
  "Sherley Tang": {
    backgroundColor: "#EF4444", // Red
    borderColor: "#DC2626"
  },
  "Sia Boon Tiong": {
    backgroundColor: "#8B5CF6", // Purple
    borderColor: "#7C3AED"
  },
  "Titus Kuan": {
    backgroundColor: "#06B6D4", // Cyan
    borderColor: "#0891B2"
  },
  "Lee Eng Kian": {
    backgroundColor: "#84CC16", // Lime
    borderColor: "#65A30D"
  },
  // Fallback colors for any additional partners
  "Unknown": {
    backgroundColor: "#6B7280", // Gray
    borderColor: "#4B5563"
  }
};

// Default chart colors for non-partner data
export const DEFAULT_CHART_COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", 
  "#06B6D4", "#84CC16", "#F97316", "#EC4899", "#6B7280",
  "#1E40AF", "#059669", "#B45309", "#B91C1C", "#6D28D9",
  "#0E7490", "#65A30D", "#C2410C", "#BE185D", "#374151"
];

/**
 * Get colors for partner data with substring matching
 * @param {Array} partnerNames - Array of partner names
 * @returns {Object} - Object with backgroundColor and borderColor arrays
 */
export const getPartnerColors = (partnerNames) => {
  const backgroundColors = [];
  const borderColors = [];
  
  partnerNames.forEach(partnerName => {
    // Find partner by substring matching (case-insensitive)
    let matchedPartner = null;
    const partnerKeys = Object.keys(PARTNER_COLORS).filter(key => key !== "Unknown");
    
    for (const partnerKey of partnerKeys) {
      if (partnerName.toLowerCase().includes(partnerKey.toLowerCase()) || 
          partnerKey.toLowerCase().includes(partnerName.toLowerCase())) {
        matchedPartner = partnerKey;
        console.log(`🎨 Partner color match: "${partnerName}" → "${matchedPartner}" (${PARTNER_COLORS[matchedPartner].backgroundColor})`);
        break;
      }
    }
    
    if (!matchedPartner) {
      console.log(`🎨 No partner match found for: "${partnerName}" → using Unknown color`);
    }
    
    const colors = matchedPartner ? PARTNER_COLORS[matchedPartner] : PARTNER_COLORS["Unknown"];
    backgroundColors.push(colors.backgroundColor);
    borderColors.push(colors.borderColor);
  });
  
  return {
    backgroundColor: backgroundColors,
    borderColor: borderColors
  };
};

/**
 * Check if data contains partner information with substring matching
 * @param {Object|Array} data - Chart data or labels array
 * @param {string} title - Chart title
 * @returns {boolean} - True if this appears to be partner data
 */
export const isPartnerData = (data, title = "") => {
  if (!data) return false;
  
  const titleLower = title.toLowerCase();
  const isPartnerChart = titleLower.includes('partner') || titleLower.includes('archival');
  
  // Handle both array and object inputs
  const keys = Array.isArray(data) ? data : Object.keys(data);
  const partnerKeys = Object.keys(PARTNER_COLORS).filter(key => key !== "Unknown");
  
  const hasPartnerNames = keys.some(key => 
    partnerKeys.some(partnerKey => 
      key.toLowerCase().includes(partnerKey.toLowerCase()) || 
      partnerKey.toLowerCase().includes(key.toLowerCase())
    )
  );
  
  return isPartnerChart || hasPartnerNames;
};

/**
 * Get colors for chart data, using partner colors if applicable
 * @param {Object|Array} data - Chart data or labels array
 * @param {string} title - Chart title
 * @param {string} type - Chart type
 * @returns {Object} - Color configuration for Chart.js
 */
export const getChartColors = (data, title = "", type = "bar") => {
  if (!data) {
    return {
      backgroundColor: DEFAULT_CHART_COLORS[0] + (type === "line" ? "00" : "80"),
      borderColor: DEFAULT_CHART_COLORS[0]
    };
  }
  
  // Handle Chart.js dataset format
  if (data.labels && data.datasets) {
    return null; // Let existing logic handle this
  }
  
  // Handle both object and array inputs
  const keys = Array.isArray(data) ? data : Object.keys(data);
  
  // For pie charts, always use normal color sequence
  if (type === "pie") {
    const numItems = keys.length;
    return {
      backgroundColor: DEFAULT_CHART_COLORS.slice(0, numItems).map(color => color + "CC"),
      borderColor: DEFAULT_CHART_COLORS.slice(0, numItems),
      borderWidth: 2
    };
  }
  
  if (isPartnerData(data, title)) {
    const partnerColors = getPartnerColors(keys);
    
    if (type === "doughnut") {
      return {
        backgroundColor: partnerColors.backgroundColor.map(color => color + "CC"),
        borderColor: partnerColors.borderColor,
        borderWidth: 2
      };
    } else if (type === "line") {
      return {
        backgroundColor: "transparent",
        borderColor: partnerColors.borderColor[0] || DEFAULT_CHART_COLORS[0],
        borderWidth: 2
      };
    } else {
      return {
        backgroundColor: partnerColors.backgroundColor.map(color => color + "80"),
        borderColor: partnerColors.borderColor,
        borderWidth: 2
      };
    }
  }
  
  // Default colors for non-partner data
  const numItems = keys.length;
  if (type === "doughnut") {
    return {
      backgroundColor: DEFAULT_CHART_COLORS.slice(0, numItems).map(color => color + "CC"),
      borderColor: DEFAULT_CHART_COLORS.slice(0, numItems),
      borderWidth: 2
    };
  } else if (type === "line") {
    return {
      backgroundColor: "transparent",
      borderColor: DEFAULT_CHART_COLORS[0],
      borderWidth: 2
    };
  } else {
    return {
      backgroundColor: DEFAULT_CHART_COLORS[0] + "80",
      borderColor: DEFAULT_CHART_COLORS[0],
      borderWidth: 2
    };
  }
}; 